import { Injectable, inject, computed } from '@angular/core';
import { Router } from '@angular/router';
import {
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_LOADING } from '../interceptors/loading.interceptor';
import { environment } from '../../environments/environment';
import { apiConfig } from '../utils/api-config.util';
import { AuthService } from '../auth/services/auth.service';
import { 
  MessageNotificationData, 
  MessageThread,
  MessagePriority,
  SimpleInAppNotification 
} from '@ics-referral-hub/shared-types';
import { catchError, throwError } from 'rxjs';
import { Logger } from '../shared/utils/logger.util';

export interface MessageNotificationSummary {
  referralId: string;
  patientName: string;
  hospitalName: string;
  programName: string;
  unreadCount: number;
  lastMessageAt: Date | string; // Date from backend is serialized as ISO string over HTTP
  highestPriority: MessagePriority;
  latestMessage: {
    senderName: string;
    preview: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class MessageNotificationService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private queryClient = inject(QueryClient);
  private router = inject(Router);
  private readonly apiPrefix = apiConfig.prefix;
  private baseUrl = `${environment.apiUrl}${this.apiPrefix}/messages`;

  // Query to get message notifications (unread + recently-read threads for the bell)
  getMessageNotificationsQuery = injectQuery(() => ({
    queryKey: ['message-notifications'],
    queryFn: (): Promise<{
      success: boolean;
      data: MessageNotificationSummary[];
    }> => {
      // Verify authentication before making the request
      if (!this.authService.isAuthenticated()) {
        return Promise.reject(new Error('User is not authenticated'));
      }

      return this.http
        .get<{
          success: boolean;
          data: MessageNotificationSummary[];
        }>(`${this.baseUrl}/notifications`, {
          context: new HttpContext().set(SKIP_LOADING, true)
        })
        .pipe(
          catchError((error: any) => {
            Logger.error('Failed to fetch message notifications:', error);
            return throwError(() => error);
          })
        )
        .toPromise() as Promise<{
        success: boolean;
        data: MessageNotificationSummary[];
      }>;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
    refetchOnWindowFocus: true, // Keep message notifications fresh when user returns (uses SKIP_LOADING)
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
  }));

  // Computed properties for easy access
  messageNotifications = computed(() => {
    return this.getMessageNotificationsQuery.data()?.data || [];
  });

  totalUnreadMessages = computed(() => {
    return this.messageNotifications().reduce((total, notification) => total + notification.unreadCount, 0);
  });

  isLoading = computed(() => {
    return this.getMessageNotificationsQuery.isLoading();
  });

  /**
   * Convert message notifications to SimpleInAppNotification format for the notification bell.
   * Read threads (unreadCount === 0) stay visible but are not highlighted as new.
   */
  getNotificationsForBell = computed((): SimpleInAppNotification[] => {
    const messageNotifications = this.messageNotifications();
    
    return messageNotifications.map((notification) => {
      // Handle lastMessageAt which comes as ISO string from backend
      const lastMessageDate = typeof notification.lastMessageAt === 'string'
        ? new Date(notification.lastMessageAt)
        : notification.lastMessageAt;

      const hasUnread = notification.unreadCount > 0;
      const messageLabel = hasUnread
        ? `New message${notification.unreadCount > 1 ? 's' : ''} for ${notification.patientName}`
        : `Message for ${notification.patientName}`;
      const detailLabel = hasUnread
        ? `${notification.unreadCount} unread message${notification.unreadCount > 1 ? 's' : ''} from ${notification.hospitalName} - ${notification.latestMessage.preview}`
        : `From ${notification.hospitalName} - ${notification.latestMessage.preview}`;

      const bellNotification: SimpleInAppNotification = {
        _id: `message-${notification.referralId}`,
        title: messageLabel,
        message: detailLabel,
        createdAt: lastMessageDate.getTime(),
        actionUrl: `/referrals/${notification.referralId}/messages`,
        isRead: !hasUnread,
        metadata: {
          referralId: notification.referralId,
          unreadCount: notification.unreadCount,
          hospitalName: notification.hospitalName,
          programName: notification.programName,
          patientName: notification.patientName,
          priority: this.mapMessagePriorityToNotificationPriority(notification.highestPriority),
          type: 'message',
        },
      };

      return bellNotification;
    });
  });

  /**
   * Handle clicking on a message notification
   */
  handleMessageNotificationClick(notification: SimpleInAppNotification): void {
    if (notification.metadata && notification.metadata['referralId']) {
      const referralId = notification.metadata['referralId'];
      const targetUrl = `/referrals/${referralId}/messages`;
      const currentUrl = this.router.url;
      const currentPath = currentUrl.split('?')[0]; // Get path without query params
      
      Logger.log('📨 Navigating to referral messages from notification:', {
        referralId: referralId,
        patientName: notification.metadata['patientName'],
        targetUrl: targetUrl,
        currentUrl: currentUrl,
        alreadyOnMessages: currentPath === targetUrl
      });
      
      // Invalidate cached referral detail so messages are refetched fresh
      this.queryClient.invalidateQueries({
        queryKey: ['referrals', 'detail', referralId],
      });
      this.queryClient.invalidateQueries({
        queryKey: ['messages', 'referral', referralId],
      });

      // If already on the messages page for this referral, invalidation above
      // will trigger a refetch — no navigation needed
      if (currentPath === targetUrl) {
        Logger.log('ℹ️ Already viewing messages for this referral — cache invalidated for refresh');
        return;
      }
      
      // Navigate to the referral messages page
      this.router.navigate(['/referrals', referralId, 'messages']).then((navigated) => {
        if (navigated) {
          Logger.log('✅ Successfully navigated to referral messages:', referralId);
        } else {
          Logger.warn('⚠️ Navigation returned false (may already be on this route):', referralId);
        }
      }).catch((error) => {
        Logger.error('❌ Navigation error:', {
          referralId,
          error: error?.message || 'Unknown error',
          currentUrl: this.router.url
        });
      });
    } else {
      Logger.error('Message notification missing referralId in metadata:', notification);
    }
  }

  /**
   * Get priority display information for message notifications
   */
  getPriorityDisplay(priority: MessagePriority): { 
    label: string; 
    color: string; 
    icon?: string;
    badgeClass?: string;
  } {
    switch (priority) {
      case MessagePriority.URGENT:
        return { 
          label: 'Urgent', 
          color: '#0b435b', 
          icon: 'pi-exclamation-triangle',
          badgeClass: 'bg-[#0b435b] text-white'
        };
      case MessagePriority.HIGH:
        return { 
          label: 'High', 
          color: '#0d4e6a', 
          icon: 'pi-exclamation',
          badgeClass: 'bg-[#0d4e6a] text-white'
        };
      case MessagePriority.NORMAL:
        return { 
          label: 'Normal', 
          color: '#0b738e',
          badgeClass: 'bg-[#0b738e] text-white'
        };
      case MessagePriority.LOW:
        return { 
          label: 'Low', 
          color: '#6b7280',
          badgeClass: 'bg-gray-500 text-white'
        };
      default:
        return { 
          label: 'Normal', 
          color: '#0b738e',
          badgeClass: 'bg-[#0b738e] text-white'
        };
    }
  }

  /**
   * Format time ago for message notifications
   */
  formatTimeAgo(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    // For older notifications, return date
    return new Date(timestamp).toLocaleDateString();
  }

  /**
   * Manually refresh message notifications
   */
  refetch(): void {
    this.getMessageNotificationsQuery.refetch();
  }

  /**
   * Invalidate message notification queries
   */
  invalidateQueries(): void {
    this.queryClient.invalidateQueries({ queryKey: ['message-notifications'] });
  }

  /**
   * Map message priority to notification priority
   */
  private mapMessagePriorityToNotificationPriority(priority: MessagePriority): 'low' | 'normal' | 'high' | 'urgent' {
    switch (priority) {
      case MessagePriority.URGENT:
        return 'urgent';
      case MessagePriority.HIGH:
        return 'high';
      case MessagePriority.NORMAL:
        return 'normal';
      case MessagePriority.LOW:
        return 'low';
      default:
        return 'normal';
    }
  }
}
